<script setup>
import { useAppSettings } from '@src/composables/useAppSettings';
import { computed, onMounted, ref, watch } from 'vue';

import { useAlert } from '@/src/composables/useAlert';

import AddPatient from '../components/AddPatient.vue';

import { startKeepAlive } from '../components/backend/backend-machine-status';
import FindPatient from '../components/FindPatient.vue';
import HomeGreetings from '../components/HomeGreetings.vue';

import HomeThumbnailContainer from '../components/HomeThumbnailContainer.vue';
import SetPassword from '../components/SetPassword.vue';
import StudyContainer from '../components/StudyContainer.vue';
import VersionNumber from '../components/VersionNumber.vue';
import { useCurrentStudyStore } from '../store/dicom-web/current-study-store';
import { useAppState } from '../store/ocg-app-state';
import { useStudiesStore } from '../store/study-list-home';
import { setPasswordRequested } from '../supabase';

// Reactive data
const appState = useAppState();
const studiesStore = useStudiesStore();
const currentStudyStore = useCurrentStudyStore();
const setPasswordRef = ref();
const showThumbnail = ref(false);
const targetStudy = ref(null);
const { loadSettings } = useAppSettings();

// Computed properties
const currentStudyID = computed(() => {
  return currentStudyStore.getStudyID;
});

// Methods
const handleUpdateFilter = (filter) => {
  studiesStore.setSearchTerm(filter);
};

const handleStudyClicked = (study) => {
  if (study.StudyInstanceUID !== targetStudy.value?.StudyInstanceUID) {
    targetStudy.value = study;
    showThumbnail.value = true;
  }
  else if (showThumbnail.value) {
    showThumbnail.value = false;
    targetStudy.value = null;
  }
};

const handleStudyDeleted = (study) => {
  if (targetStudy.value?.StudyInstanceUID === study.StudyInstanceUID) {
    showThumbnail.value = false;
    targetStudy.value = null;
  }
};

const loadUserSettings = async () => {
  await loadSettings();
};

// Lifecycle hooks
onMounted(() => {
  startKeepAlive('segmentation');
  loadUserSettings();
  const alert = useAlert();
  alert.hideAll();
  // if (currentStudyID.value) {
  //   targetStudy.value = currentStudyID.value;
  //   showThumbnail.value = true;
  // }
  studiesStore.resetSearchTerm();

  watch(
    () => !!setPasswordRequested.value,
    (newValue) => {
      if (newValue) {
        setPasswordRequested.value = false;
        setPasswordRef.value.open();
      }
    },
    { immediate: true },
  );
});

// Watchers

watch(
  () => !!appState.session,
  (newValue) => {
    if (!newValue) {
      // router.push('/login');
    }
  },
  { immediate: true },
);
</script>

<template>
  <div
    class="flex flex-col px-10 py-2 h-full w-full"
    style="border-radius: 0px"
  >
    <SetPassword ref="setPasswordRef" />
    <div class="flex flex-col flex-1 min-h-0 overflow-hidden">
      <div class="flex flex-col gap-6 mb-4">
        <HomeGreetings :username="appState.currentUserName" />
        <div class="flex align-center justify-center gap-4">
          <v-btn
            v-if="currentStudyID"
            icon="mdi-arrow-left"
            size="small"
            variant="tonal"
            :title="`Return to Analysis - ${
              currentStudyStore.getStudyInfo?.PatientName || ''
            }`"
            @click="$router.push('/analysis')"
          />
          <FindPatient @search-term-updated="handleUpdateFilter" />
          <AddPatient />
        </div>
      </div>
      <div class="flex flex-1 min-h-0 overflow-hidden gap-2">
        <div class="flex-1 min-h-0 overflow-y-auto">
          <StudyContainer
            :target-study="targetStudy?.StudyInstanceUID"
            @study-clicked="handleStudyClicked"
            @study-deleted="handleStudyDeleted"
          />
        </div>
        <div v-if="showThumbnail" class="vertical-divider" />
        <div class="container" :class="{ opened: showThumbnail }">
          <HomeThumbnailContainer
            v-if="showThumbnail"
            :key="targetStudy?.StudyInstanceUID"
            :study="targetStudy"
          />
        </div>
      </div>
    </div>
    <VersionNumber :margin-bot="34" />
  </div>
</template>

<style scoped>
.container {
  width: 0;
  opacity: 0;
  overflow: hidden;
  transition:
    width 0.5s ease,
    opacity 0.4s ease;
  transition-delay: 0s, 0.2s;
}

.container.opened {
  width: 40%;
  opacity: 1;
  min-height: 0;
  overflow-y: auto;
}
</style>
